'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface Props {
  role: 'player' | 'admin';
}

const DEMO_CREDENTIALS = {
  player: { email: 'striker99@fishblast.gg', password: 'Blast#2026' },
  admin: { email: 'operator@fishblast.gg', password: 'Admin#2026' },
};

export default function LoginForm({ role }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, formState: { errors }, setError, setValue } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  // Expose setValue for DemoCredentials autofill
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, unknown>).__loginSetValue = setValue;
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password, role }
    await new Promise((r) => setTimeout(r, 1200));

    const demo = DEMO_CREDENTIALS[role];
    if (data.email === demo.email && data.password === demo.password) {
      toast.success(`Welcome back! Redirecting to ${role === 'admin' ? 'Admin Panel' : 'Game'}...`);
      setTimeout(() => {
        router.push(role === 'admin' ? '/admin-panel' : '/game-screen');
      }, 800);
    } else {
      setError('root', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
      {/* Root error */}
      {errors.root && (
        <div className="rounded-lg px-4 py-3 text-sm animate-fade-in"
          style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
          {errors.root.message}
        </div>
      )}

      {/* Email */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-foreground" htmlFor="login-email">
          Email Address
        </label>
        <input
          id="login-email"
          type="email"
          autoComplete="email"
          placeholder="your@email.com"
          className={`w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200 ${
            errors.email ? 'border-red-500' : 'focus:neon-border-cyan'
          }`}
          style={{
            background: 'var(--input)',
            border: `1px solid ${errors.email ? '#ef4444' : 'var(--border)'}`,
            color: 'var(--foreground)',
          }}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="text-xs" style={{ color: '#f87171' }}>{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground" htmlFor="login-password">
            Password
          </label>
          <button type="button" className="text-xs neon-text-cyan hover:opacity-80 transition-opacity">
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            className="w-full px-4 py-2.5 pr-11 rounded-lg text-sm outline-none transition-all duration-200"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.password ? '#ef4444' : 'var(--border)'}`,
              color: 'var(--foreground)',
            }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-xs" style={{ color: '#f87171' }}>{errors.password.message}</p>}
      </div>

      {/* Remember me */}
      <label className="flex items-center gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          className="w-4 h-4 rounded accent-primary"
          {...register('remember')}
        />
        <span className="text-sm text-muted-foreground">Remember me for 30 days</span>
      </label>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary flex items-center justify-center gap-2 py-3 px-6 rounded-lg text-sm w-full disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ minWidth: '160px' }}
      >
        {isLoading ? (
          <><Loader2 size={16} className="animate-spin" /> Signing in...</>
        ) : (
          <><LogIn size={16} /> Sign In to {role === 'admin' ? 'Admin Panel' : 'Game Lobby'}</>
        )}
      </button>
    </form>
  );
}